/**
 * Config module exports
 * Provides access to auth and configuration utilities
 */

import type { OktaAuth } from "@okta/okta-auth-js";
import { getOktaAuth } from "@fmc-digital/core-auth";
import { loadConfig, type RuntimeConfig } from "./auth.config";

/** Targets for legacy env key → config lookup (avoids `keyof RuntimeConfig`, which is `string` due to index signature). */
type ConfigEnvMapValue =
  | keyof RuntimeConfig["api"]
  | "googleRecaptchaSiteKey"
  | "registerNowUrl"
  | "mobileAppDownloadUrl"
  | "cdnRootUrl";

// Legacy auth object wrapper for backward compatibility
type OktaInstance = OktaAuth | { getAccessToken: () => null };
let oktaInstance: OktaInstance | null = null;

export const auth = {
  get isNotDefined() {
    try {
      oktaInstance = getOktaAuth();
      return false;
    } catch {
      return true;
    }
  },
  getOkta: () => {
    if (!oktaInstance) {
      try {
        oktaInstance = getOktaAuth();
      } catch {
        // Return a mock object if Okta is not initialized
        return {
          getAccessToken: () => null,
        };
      }
    }
    return oktaInstance;
  },
};

// Legacy config object wrapper
let cachedConfig: RuntimeConfig | null = null;

// Runtime values storage (for tokens, timers, etc. that are set at runtime)
const runtimeValues: Record<string, unknown> = {};

const TOP_LEVEL_OAUTH_ENV_GETTERS: Record<string, (c: RuntimeConfig) => string> = {
  oauthIssuer: (c) => c.oauthIssuer ?? "",
  oauthClientId: (c) => c.oauthClientId ?? "",
  dashboardRedirectUri: (c) => c.dashboardRedirectUri ?? "",
  oauthAuthorizeScope: (c) => c.oauthAuthorizeScope ?? "",
};

function resolveMappedLegacyEnv(
  cfg: RuntimeConfig,
  mappedKey: ConfigEnvMapValue | undefined,
): string | undefined {
  if (mappedKey === undefined) {
    return undefined;
  }
  if (mappedKey === "googleRecaptchaSiteKey") {
    return cfg.googleRecaptchaSiteKey ?? "";
  }
  if (mappedKey === "registerNowUrl") {
    return cfg.registerNowUrl ?? "";
  }
  if (mappedKey === "mobileAppDownloadUrl") {
    return cfg.mobileAppDownloadUrl ?? "";
  }
  if (mappedKey === "cdnRootUrl") {
    return (cfg as Record<string, unknown>).cdnRootUrl as string ?? "";
  }
  if (cfg.api) {
    return cfg.api[mappedKey] ?? "";
  }
  return undefined;
}

export const config = {
  env: (key: string): string => {
    // First check runtime values (tokens, timers, etc.)
    if (key in runtimeValues) {
      return String(runtimeValues[key]);
    }

    if (!cachedConfig) {
      // Try to get config synchronously (will fail if not loaded)
      console.warn("Config not loaded yet. Call loadConfig() first.");
      return "";
    }

    const cfg = cachedConfig;
    const oauthGetter = TOP_LEVEL_OAUTH_ENV_GETTERS[key];
    if (oauthGetter) {
      return oauthGetter(cfg);
    }

    // Map legacy keys to new config structure
    const keyMap: Record<string, ConfigEnvMapValue> = {
      xApiKey: "xApiKey",
      xAPIKey: "xApiKey",
      baseUrl: "baseUrl",
      mfaListUrlUsernameWebAct: "baseUrl",
      unAuthAPIUrlWebAct: "baseUrl",
      triggerOtpUrlWebAct: "baseUrl",
      googleRecaptchaSiteKey: "googleRecaptchaSiteKey",
      registerNowUrl: "registerNowUrl",
      mobileAppDownloadUrl: "mobileAppDownloadUrl",
      cdnRootUrl: "cdnRootUrl",
    };

    const mappedLegacy = resolveMappedLegacyEnv(cfg, keyMap[key]);
    if (mappedLegacy !== undefined) {
      return mappedLegacy;
    }

    // Fallback: try direct access
    return ((cfg as Record<string, unknown>)[key] as string) ?? "";
  },
  // Set runtime environment values (tokens, timers, etc.)
  setEnv: (key: string, value: unknown): void => {
    runtimeValues[key] = value;
  },
  // Initialize config cache
  init: async (runtimeConfig?: RuntimeConfig) => {
    cachedConfig = runtimeConfig ?? (await loadConfig());
  },
  // Clear config cache (useful when environment changes)
  clearCache: () => {
    cachedConfig = null;
  },
  // Get current cached config (for debugging)
  getCachedConfig: () => cachedConfig,
};

// Config will be initialized by App.tsx on mount
// Removed auto-initialization to prevent duplicate loading
